import { Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { Sentry, sentrySetFatalScope } from 'lib-shared-sentry';
import { createRef, PureComponent } from 'react';

import styles from './messageIframe.module.scss';
import {
  getNextSiblings,
  minimizeQuotes,
  OUTLOOK_QUOTES_STYLES,
  QUOTE_SELECTORS,
  wrapElementsInDixaQuote,
} from './utils';

type MessageIframeProps = {
  /** Extra classnames */
  className?: string;
  /** Iframe source content */
  content: string;
  /** Extra iframe height */
  extraIframeheight?: number;
  /** True if content is plain text rather than html */
  isPlainText?: boolean;
};

type StateT = {
  iframeHeight: number;
};

function wrapQuoteWithXpath(xpath: string, document: Document) {
  const xpathResultOutlook = document.evaluate(
    xpath,
    document,
    null,
    XPathResult.FIRST_ORDERED_NODE_TYPE,
    null,
  ).singleNodeValue;

  if (xpathResultOutlook) {
    const parentElm = xpathResultOutlook.parentElement;
    const nextElms = getNextSiblings(
      parentElm?.parentElement === document.body
        ? xpathResultOutlook
        : parentElm,
    );

    wrapElementsInDixaQuote(nextElms);
  }
}

export class MessageIframe extends PureComponent<MessageIframeProps, StateT> {
  static defaultProps: {
    /** Extra iframe height */
    extraIframeheight: number;
  };

  frameRef: {
    current: null | HTMLIFrameElement;
  };

  mutationObserver: null | MutationObserver;

  _isMounted: boolean;

  constructor(props: MessageIframeProps) {
    super(props);

    this.state = {
      iframeHeight: 0,
    };

    this.frameRef = createRef();
    this.mutationObserver = null;
    this._isMounted = true;
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.resizeFrame = this.resizeFrame.bind(this);
  }

  componentDidMount() {
    const { content, isPlainText } = this.props;
    const iframe = this.getIframeWindow();

    if (iframe?.document?.body) {
      iframe.document.open();
      iframe.document.close();
      iframe.document.body.style.padding = '0';
      iframe.document.body.style.margin = '0';
      // TODO: [TEAMS-12078] Investigate improper use of innerHTML
      iframe.document.body.innerHTML = isPlainText
        ? `<div style="white-space: pre-wrap">${content}</div>`
        : content;

      // Add base tag to head.
      const base = iframe.document.createElement('base');
      base.setAttribute('target', '_parent');
      iframe.document.getElementsByTagName('head')[0].appendChild(base);

      // Add loader to all images.
      iframe.document.body.querySelectorAll('img').forEach((img) => {
        img.addEventListener('load', this.resizeFrame);
      });
      // Add target _blank to all a tags.
      iframe.document.body.querySelectorAll('a').forEach((a) => {
        a.setAttribute('target', '_blank');
      });

      OUTLOOK_QUOTES_STYLES.map((style) => {
        wrapQuoteWithXpath(
          `//div[contains(@style, '${style}')]`,
          iframe.document,
        );
      });

      // Add custom styling and custom script.
      // Styling is for hiding the quotes and script for the toggling logic.
      const style = iframe.document.createElement('style');
      // TODO: [TEAMS-12078] Investigate improper use of innerHTML
      style.innerHTML = `
        ${QUOTE_SELECTORS.join(',')},
        .hidden {
          display: none;
        }
        .show {
          display: block !important;
        }
      `;
      iframe.document.getElementsByTagName('head')[0].appendChild(style);

      minimizeQuotes(iframe.document, iframe.document);

      this.resizeFrame();
      iframe.window.addEventListener('onmouseup', this.handleWindowResize);
      window.addEventListener('resize', this.handleWindowResize);

      this.mutationObserver = new MutationObserver(() => {
        this.resizeFrame();
      });
      if (iframe && iframe.document.body) {
        this.mutationObserver.observe(iframe.document.body, {
          attributes: true,
          childList: true,
          subtree: true,
        });
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    window.removeEventListener('resize', this.handleWindowResize);

    if (this.mutationObserver) this.mutationObserver.disconnect();

    const iframe = this.getIframeWindow();
    if (iframe && iframe.document.body) {
      iframe.window.removeEventListener('onmouseup', this.handleWindowResize);
      iframe.document.body.querySelectorAll('img').forEach((img) => {
        img.removeEventListener('load', this.resizeFrame);
      });
    }
  }

  handleWindowResize(): void {
    if (!this._isMounted) return;

    this.setState({
      iframeHeight: 0,
    });
    this.resizeFrame();
  }

  getIframeWindow(): null | Window {
    const { current } = this.frameRef;
    return current ? current.contentWindow : null;
  }

  resizeFrame(): void {
    if (!this._isMounted) return;
    const { extraIframeheight } = this.props;

    this.setState(
      {
        iframeHeight: 0,
      },
      () => {
        const iframe = this.getIframeWindow();
        if (iframe && iframe.document.body) {
          this.setState({
            iframeHeight:
              iframe.document.body.scrollHeight + (extraIframeheight || 0),
          });
        }
      },
    );
  }

  render() {
    const {
      className,
      content,
      extraIframeheight,
      isPlainText,
      ...otherProps
    } = this.props;

    const { iframeHeight } = this.state;

    const rootClass = classnames(className);

    return (
      <div {...otherProps} className={rootClass}>
        <iframe
          title="message"
          scrolling="no"
          frameBorder="no"
          style={{ height: `${iframeHeight}px` }}
          className={styles.container}
          ref={this.frameRef}
        />
      </div>
    );
  }
}

export function MessageIframeWithErrorBoundary(
  props: MessageIframeProps,
): JSX.Element {
  return (
    <Sentry.ErrorBoundary
      beforeCapture={sentrySetFatalScope}
      fallback={
        <Text.Input variant="error">
          An error occurred and we could not display this message.
        </Text.Input>
      }
    >
      <MessageIframe {...props} />
    </Sentry.ErrorBoundary>
  );
}

MessageIframe.defaultProps = {
  extraIframeheight: 20,
};
